import { useRef, useEffect, useState } from 'react';
import { check_circle_primary } from '@assets/icons';
import {
  ChevronLeftWhiteIcon,
  ChevronLeftWhiteTransparentIcon,
  ChevronRightGray400Icon,
  ChevronRightWhiteIcon,
  ChevronRightWhiteTransparentIcon,
  DragAndDropIcon,
  FlagErrorIcon,
  FlagGray300Icon,
  PictureIcon,
  StarsSecondaryIcon,
  TrashCanPrimaryIcon,
  XGray400Icon
} from '@assets/iconComponents';
import {
  AIRecommendedChildOptionValue,
  AIRecommendedTag,
  Chip,
  Frame,
  GuideModal,
  AdminCharacterNumberInput,
  AdminIssueReportModal,
  AdminMenuTop,
  LoadingSpinner,
  SearchInput,
  SecureImage,
  Select,
  Text
} from '@components';
import {
  ChipPaperType,
  FemaleBodyChildOptionValueEnum,
  HorizontalAngleChildOptionValueEnum,
  AdminModalType,
  AdultsOnlyShotChildOptionValueEnum,
  SexualBodyPartChildOptionValueEnum,
  SexualPositionChildOptionValueEnum,
  styleConstant,
  VerticalAngleChildOptionValueEnum,
  ToastTypeEnum,
  ToastMessageEnum,
  CategorySelectEnum,
  AgeRangeChildOptionValueEnum,
  MainMotionPartChildOptionValueEnum,
  StandardFacialExpressionChildOptionValueEnum,
  PostureCategoryChildOptionValueEnum,
  StandardShotChildOptionValueEnum
} from '@constant';
import { useAdminImageUpsert, useIssueReport, useToast } from '@hooks';
import { Button, MenuItem, styled } from '@mui/material';
import { colorSet } from '@theme';
import { IOptionList } from '@types';

// 스크롤바 커스터마이징을 위해 MUI styled component 이용
const ScrollList = styled('div')({
  height: '100%',
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    width: '8px',
    height: '0px'
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: colorSet.primaryMain
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: '8px',
    backgroundColor: colorSet.gray200
  },

  '&.has-scrollbar': {
    paddingRight: '8px'
  }
});

const ImageUpsertPage = () => {
  const {
    getBreadcrumbInfo,
    handleBreadCrumbClick,

    isCreate,
    handlePageCancelClick,
    needImageAttach,
    isRequestPending,
    validateImageChildOptions,
    handleSaveButtonClick,

    category,
    handleCategoryChange,
    selectedSexualPosition,
    handleSelectedSexualPositionChange,
    inputKeyword,
    autocompleteList,
    handleInputKeywordChange,
    handleAutocompleteChange,
    handleComicNameUpdate,

    imageList,
    handleImageUploadButtonClick,
    handleAttachDrop,
    handleAttachDragOver,
    handleAttachDragEnter,
    handleDragStart,
    handleDragOver,
    handleDragDrop,

    handleImageFocus,
    focusedImage,
    focusedImageIndex,
    handleImageDelete,
    getImageIssueReportList,
    updateCheckedIssueReportIds,
    updateFocusedImageChildOptions,
    updateDependentChildOptions,
    isFetchImageAIRecommendationLoading,
    tagInputKeyword,
    tagAutocompleteList,
    handleTagKeyDown,
    handleTagInputKeywordChange,
    handleTagAutocompleteChange,
    addFocusedImageTag,
    deleteFocusedImageTag,

    modalType,
    isModalVisible,
    setIsModalVisible,
    modalTitle,
    modalContent,
    handleModalConfirmClick,
    isSaveAttempted
  } = useAdminImageUpsert();

  const { isIssueReportModalVisible, setIsIssueReportModalVisible } = useIssueReport();

  // 스크롤 리스트 스크롤바 여부에 따른 CSS 분기를 위한 상태
  const [hasScrollbar, setHasScrollbar] = useState(false);

  const isStandard = category === CategorySelectEnum.STANDARD;

  const scrollListRef = useRef<HTMLDivElement>(null);

  const horizontalGap = 2;
  const itemNumberPerRow = 5;
  const sumOfHorizontalGaps = horizontalGap * (itemNumberPerRow - 1);
  const itemSize = `calc((100% - ${sumOfHorizontalGaps}px) / ${itemNumberPerRow})`;

  // 인원수 의존성 필터 disabled 로직을 위한 변수
  const isCharacterNumberOne = (() => {
    const characterNumber = focusedImage?.childOptions?.characterNumber;

    if (!characterNumber || typeof characterNumber !== 'object' || Array.isArray(characterNumber)) {
      return false;
    }

    const hasMaleNumber = 'maleNumber' in characterNumber;
    const hasFemaleNumber = 'femaleNumber' in characterNumber;

    if (!hasMaleNumber || !hasFemaleNumber) {
      return false;
    }

    const { maleNumber, femaleNumber } = characterNumber as {
      maleNumber: number | string;
      femaleNumber: number | string;
    };
    return typeof maleNumber === 'number' && typeof femaleNumber === 'number' && maleNumber + femaleNumber === 1;
  })();

  const { showToast } = useToast();

  const createModalButtonText = () => {
    if (modalType === AdminModalType.IMAGE_UPLOAD_CANCEL || modalType === AdminModalType.IMAGE_EDIT_CANCEL) {
      return '중단하기';
    } else if (modalType === AdminModalType.IMAGE_DELETE_CONFIRM) {
      return '삭제하기';
    } else {
      return '확인';
    }
  };

  const createModalButtonVariant = () => {
    if (
      modalType === AdminModalType.IMAGE_UPLOAD_CANCEL ||
      modalType === AdminModalType.IMAGE_EDIT_CANCEL ||
      modalType === AdminModalType.IMAGE_DELETE_CONFIRM ||
      modalType === AdminModalType.IMAGE_UPLOAD_SUCCESS ||
      modalType === AdminModalType.IMAGE_EDIT_SUCCESS
    ) {
      return 'contained';
    } else {
      return 'outlined';
    }
  };

  const createModalButtonCount = () => {
    if (
      modalType === AdminModalType.IMAGE_UPLOAD_CANCEL ||
      modalType === AdminModalType.IMAGE_EDIT_CANCEL ||
      modalType === AdminModalType.IMAGE_DELETE_CONFIRM
    ) {
      return 2;
    } else {
      return 1;
    }
  };

  // 이미지 리스트의 스크롤바 존재 여부 체크 (imageList 변동시)
  useEffect(() => {
    const checkScrollbar = () => {
      if (scrollListRef.current) {
        const hasVerticalScrollbar = scrollListRef.current.scrollHeight > scrollListRef.current.clientHeight;
        setHasScrollbar(hasVerticalScrollbar);
      }
    };

    checkScrollbar();
    window.addEventListener('resize', checkScrollbar);
    return () => window.removeEventListener('resize', checkScrollbar);
  }, [imageList]);

  return (
    <>
      <AdminMenuTop />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: `calc(100vh - ${styleConstant.menuTopHeight}px)`
        }}
      >
        {/* 상단부: 헤더 및 필터&작품명 */}
        <Frame
          width="100%"
          padding="24px 40px 24px 40px"
          boxstyle={{
            position: 'fixed',
            left: 0,
            right: 0,
            top: styleConstant.menuTopHeight,
            zIndex: styleConstant.zIndexTopFilter
          }}
        >
          {/* 헤더 */}
          <div
            style={{
              height: 56,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-end'
            }}
          >
            <div>
              <div
                onClick={handleBreadCrumbClick}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: '4px',
                  cursor: 'pointer'
                }}
              >
                <Text variant="body1" color="primary" marginRight="10px">
                  {getBreadcrumbInfo().text}
                </Text>
                <ChevronRightGray400Icon style={{ width: 16, height: 16 }} />
              </div>
              <Text variant="h2" fontWeight="bold">
                {isCreate ? '이미지 업로드' : '이미지 정보 수정'}
              </Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 40 }}>
              {/* 필터&작품명 */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 24
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text variant="subtitle" fontWeight="bold">
                    테마
                  </Text>
                  <Select
                    disabled={!isCreate}
                    value={category}
                    onChange={handleCategoryChange}
                    boxstyle={{ width: 141 }}
                  >
                    <MenuItem value={CategorySelectEnum.STANDARD}>{CategorySelectEnum.STANDARD}</MenuItem>
                    <MenuItem value={CategorySelectEnum.ADULTS_ONLY}>{CategorySelectEnum.ADULTS_ONLY}</MenuItem>
                  </Select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text variant="subtitle" fontWeight="bold">
                    장르
                  </Text>
                  <Select disabled value={'남성향 19금'} boxstyle={{ width: 133 }}>
                    <MenuItem value="남성향 19금">남성향 19금</MenuItem>
                  </Select>
                </div>
                {isStandard ? null : (
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text variant="subtitle" fontWeight="bold">
                      체위
                    </Text>
                    <Select
                      value={selectedSexualPosition}
                      onChange={handleSelectedSexualPositionChange}
                      boxstyle={{ width: 141 }}
                    >
                      <MenuItem value={'체위'} disabled>
                        체위
                      </MenuItem>
                      {Object.entries(SexualPositionChildOptionValueEnum)
                        .filter(([label]) => label !== '모든 체위')
                        .map(([label, value]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                    </Select>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text variant="subtitle" fontWeight="bold">
                    작품명
                  </Text>
                  <SearchInput
                    placeholder="필수 입력란입니다."
                    boxstyle={{ width: 220 }}
                    value={inputKeyword}
                    options={autocompleteList}
                    onInputChange={handleInputKeywordChange}
                    onAutocompleteChange={handleAutocompleteChange}
                    onComicNameUpdate={handleComicNameUpdate}
                    isError={isSaveAttempted && inputKeyword.trim().length === 0}
                    showOneSubIcon={true}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', gap: 12 }}>
                <Button
                  onClick={handlePageCancelClick}
                  variant="contained"
                  color="secondary"
                  sx={{ width: '90px', height: '44px' }}
                >
                  취소
                </Button>
                <Button
                  disabled={needImageAttach || isRequestPending}
                  onClick={handleSaveButtonClick}
                  variant="contained"
                  color="primary"
                  sx={{ width: '90px', height: '44px' }}
                >
                  저장
                </Button>
              </div>
            </div>
          </div>
        </Frame>
        <div
          style={{ height: styleConstant.topFilterImageUpsertPage, borderBottom: `1px solid ${colorSet.gray300}` }}
        />

        {/* 하단부: 파일 선택 버튼 & 이미지 보드 및 이미지 라벨링 폼 */}
        <div style={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'flex-start', flexGrow: 1 }}>
          {/* 파일 선택 버튼 & 이미지 보드 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              boxSizing: 'border-box'
            }}
          >
            <div
              style={{
                height: styleConstant.fileSelectButtonLayoutImageUpsertPage,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                padding: '16px 16px 16px 40px',
                boxSizing: 'border-box'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12
                }}
              >
                <PictureIcon style={{ width: 44, height: 44 }} />
                <div>
                  <Text variant="body1" color={colorSet.gray500} fontWeight="bold" marginBottom="4px">
                    파일을 아래에 끌어오세요.
                  </Text>
                  <Text variant="body1" color={colorSet.gray500}>
                    10MB 이하의 jpg, png 파일만 업로드 가능합니다.
                  </Text>
                </div>
              </div>

              <input
                type="file"
                multiple
                accept="image/jpg, image/png"
                key={isFetchImageAIRecommendationLoading ? 'loading' : 'ready'} // isFetchImageAIRecommendationLoading이 true일 때 첨부 시도했던 이미지가 로딩 종료 후 정상 첨부되도록 함
                onChange={(e) => {
                  // AI 추천값 로딩시 추가 첨부 방지
                  if (isFetchImageAIRecommendationLoading) {
                    showToast(
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Text variant="body1" color={colorSet.white} fontWeight="bold">
                          {ToastMessageEnum.CANNOT_ATTACH_IMAGE_DURING_AI_FETCHING}
                        </Text>
                        <Text variant="body1" color={colorSet.white}>
                          {ToastMessageEnum.RETRY_AFTER_AI_FETCHING}
                        </Text>
                      </div>,
                      ToastTypeEnum.ERROR
                    );
                  } else {
                    handleImageUploadButtonClick(e);
                  }
                }}
                style={{ display: 'none' }}
                id="image-upload-input"
              />
              <Button
                variant="contained"
                color="point"
                sx={{ width: 147, borderRadius: '22px' }}
                onClick={() => document.getElementById('image-upload-input')?.click()}
              >
                파일 직접 선택
              </Button>
            </div>

            {/* 이미지 보드 */}
            <div
              onDrop={(e) => {
                // 기본 동작 방지 (새 탭 열림 방지)
                e.preventDefault();
                e.stopPropagation();

                // AI 추천값 로딩시 추가 첨부 방지
                if (isFetchImageAIRecommendationLoading) {
                  showToast(
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Text variant="body1" color={colorSet.white} fontWeight="bold">
                        {ToastMessageEnum.CANNOT_ATTACH_IMAGE_DURING_AI_FETCHING}
                      </Text>
                      <Text variant="body1" color={colorSet.white}>
                        {ToastMessageEnum.RETRY_AFTER_AI_FETCHING}
                      </Text>
                    </div>,
                    ToastTypeEnum.ERROR
                  );
                } else {
                  handleAttachDrop(e);
                }
              }}
              onDragOver={handleAttachDragOver}
              onDragEnter={handleAttachDragEnter}
              style={{
                width: '100%',
                height: `calc(100vh - ${styleConstant.menuTopHeight + styleConstant.topFilterImageUpsertPage + styleConstant.fileSelectButtonLayoutImageUpsertPage}px)`,
                backgroundColor: colorSet.gray100
              }}
            >
              {imageList && imageList.length > 0 ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'row'
                  }}
                >
                  <div style={{ width: 634, height: '100%', padding: '16px', backgroundColor: colorSet.gray200 }}>
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative'
                      }}
                    >
                      {focusedImage && focusedImage.url ? (
                        <SecureImage
                          imageUrl={focusedImage.url}
                          imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : null}

                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div
                          onClick={() => {
                            if (focusedImageIndex !== null && focusedImageIndex > 0) {
                              handleImageFocus(focusedImageIndex - 1);
                            }
                          }}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: '20px',
                            backgroundColor:
                              focusedImageIndex === null || focusedImageIndex === 0
                                ? 'rgba(15, 15, 15, 0.3)'
                                : 'rgba(15, 15, 15, 0.6)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: focusedImageIndex === null || focusedImageIndex === 0 ? 'default' : 'pointer'
                          }}
                        >
                          {focusedImageIndex === null || focusedImageIndex === 0 ? (
                            <ChevronLeftWhiteTransparentIcon />
                          ) : (
                            <ChevronLeftWhiteIcon />
                          )}
                        </div>
                        <div
                          onClick={() => {
                            if (focusedImageIndex !== null && focusedImageIndex < imageList.length - 1) {
                              handleImageFocus(focusedImageIndex + 1);
                            }
                          }}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: '20px',
                            backgroundColor:
                              focusedImageIndex === null || focusedImageIndex === imageList.length - 1
                                ? 'rgba(15, 15, 15, 0.3)'
                                : 'rgba(15, 15, 15, 0.6)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor:
                              focusedImageIndex === null || focusedImageIndex === imageList.length - 1
                                ? 'default'
                                : 'pointer'
                          }}
                        >
                          {focusedImageIndex === null || focusedImageIndex === imageList.length - 1 ? (
                            <ChevronRightWhiteTransparentIcon />
                          ) : (
                            <ChevronRightWhiteIcon />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      height: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{ height: '100%', padding: hasScrollbar ? '16px 16px 16px 24px' : '16px 24px' }}>
                      <ScrollList ref={scrollListRef} className={hasScrollbar ? 'show-scrollbar' : ''}>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: `10px ${horizontalGap}px`,
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        >
                          {imageList.map((item, index) => {
                            return (
                              <div
                                key={index}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDragDrop(e, index)}
                                onClick={() => handleImageFocus(index)}
                                style={{
                                  position: 'relative',
                                  flex: `0 0 ${itemSize}`,
                                  width: itemSize,
                                  aspectRatio: '160 / 194',
                                  overflow: 'hidden',
                                  padding: '10px',
                                  cursor: 'grab', // 드래그 가능함을 표시하는 커서
                                  transition: 'transform 0.2s', // 부드러운 움직임
                                  backgroundColor: item.isFocus ? 'rgba(15, 15, 15, 0.06)' : 'transparent'
                                }}
                              >
                                <div
                                  style={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    borderRadius: '12px',
                                    backgroundColor:
                                      isSaveAttempted && !validateImageChildOptions(imageList[index])
                                        ? colorSet.errorMain
                                        : colorSet.gray600
                                  }}
                                >
                                  <Text variant="body2" color="white" fontWeight="bold">
                                    {item.order ?? 1}
                                  </Text>
                                </div>
                                <div style={{ width: '100%', height: 'calc(100% - 24px)' }}>
                                  {item.imageId ? (
                                    <SecureImage
                                      imageUrl={item.url ?? ''}
                                      imageStyle={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        pointerEvents: 'none' // 이미지 자체는 드래그되지 않도록
                                      }}
                                    />
                                  ) : (
                                    <img
                                      src={item.url}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        pointerEvents: 'none' // 이미지 자체는 드래그되지 않도록
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollList>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      width: 180,
                      height: 216,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    <DragAndDropIcon style={{ width: 180, height: 180 }} />
                    <Text variant="h2" color={colorSet.gray300}>
                      Drag & Drop
                    </Text>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 우측 라벨링 폼 */}
          <div
            className="hide-scrollbar"
            style={{
              width: 430,
              height: `calc(100vh - ${styleConstant.menuTopHeight + styleConstant.topFilterImageUpsertPage}px)`,
              padding: '24px 40px 40px 40px',
              borderLeft: `1px solid ${colorSet.gray300}`,
              overflowY: 'auto'
            }}
          >
            {focusedImage ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}
                >
                  <Text variant="subtitle" fontWeight="bold">
                    {/* 2장 이상의 이미지 존재 시 order 보여주기 */}
                    {focusedImage && imageList && imageList.length > 1 ? focusedImage.order : ''}
                  </Text>

                  <div style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
                    {/* 기존에 업로드된 이미지만 신고 버튼 보여주기 */}
                    {focusedImage.imageId ? (
                      <Button
                        disabled={Boolean(!focusedImage.isReported)}
                        onClick={() => {
                          setIsIssueReportModalVisible(true);
                        }}
                        variant="outlined"
                        color="error"
                        style={{ width: 64, height: 32 }}
                      >
                        {focusedImage.isReported ? (
                          <FlagErrorIcon style={{ width: 16, height: 16 }} />
                        ) : (
                          <FlagGray300Icon style={{ width: 16, height: 16 }} />
                        )}
                      </Button>
                    ) : null}
                    <Button
                      onClick={() => {
                        handleImageDelete(Boolean(focusedImage.file));
                      }}
                      variant="outlined"
                      color="primary"
                      style={{ width: 64, height: 32 }}
                    >
                      <TrashCanPrimaryIcon style={{ width: 16, height: 16 }} />
                    </Button>
                  </div>
                </div>
                <div>
                  <Text variant="subtitle" fontWeight="bold" marginBottom="8px">
                    파일명
                  </Text>
                  <Text variant="body1">{focusedImage.name}</Text>
                </div>
                {/* 필터 start */}
                <div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end',
                      marginBottom: '8px'
                    }}
                  >
                    <Text variant="subtitle" fontWeight="bold">
                      필터
                    </Text>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                      <StarsSecondaryIcon />
                      <Text variant="body1" color={colorSet.gray500}>
                        AI 추천 필터
                      </Text>
                    </div>
                  </div>
                  {isStandard ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="배경컷"
                          needTooltip={true}
                          tooltipText={`배경컷이 포함된 경우, 다음 항목은 선택할 수 없습니다.
                
            인원수 / 연령대 / 스킨십 / 중심 동작 부위 / 표정 / 가로앵글 / 데포르메 / 동작구분`}
                          options={{
                            해당: { name: '해당', value: 'true', isSelected: false },
                            미해당: { name: '미해당', value: 'false', isSelected: false }
                          }}
                          storedChildOptionValue={
                            (focusedImage?.childOptions?.['backgroundCut'] as IOptionList) ?? null
                          }
                          setSelectedOption={(param) => {
                            updateFocusedImageChildOptions('backgroundCut', param);
                            updateDependentChildOptions('backgroundCut', param);
                          }}
                          isError={isSaveAttempted && focusedImage?.childOptions?.backgroundCut == null}
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={!(focusedImage?.childOptions?.['backgroundCut'] as IOptionList)}
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['backgroundCut'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['backgroundCut'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'backgroundCut',
                              focusedImage?.aiRecommendedChildOptions?.['backgroundCut'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <AdminCharacterNumberInput
                          disabled={Boolean(
                            (focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected
                          )}
                          needTooltip={true}
                          tooltipText="인원수가 1명일 경우, 스킨십 항목은 선택할 수 없습니다."
                          characterNumber={
                            typeof focusedImage?.childOptions?.characterNumber === 'object' &&
                            focusedImage?.childOptions?.characterNumber !== null
                              ? (focusedImage.childOptions.characterNumber as {
                                  maleNumber: number | string;
                                  femaleNumber: number | string;
                                })
                              : null
                          }
                          onApplyClick={(values) => {
                            updateFocusedImageChildOptions('characterNumber', values);
                            updateDependentChildOptions(
                              'isCharacterNumberOne',
                              typeof values.maleNumber === 'number' &&
                                typeof values.femaleNumber === 'number' &&
                                values.maleNumber + values.femaleNumber === 1
                            );
                          }}
                          onChipXButtonClick={() => {
                            updateFocusedImageChildOptions('characterNumber', null);
                          }}
                          isError={
                            isSaveAttempted &&
                            focusedImage?.childOptions?.characterNumber == null &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={
                            !(focusedImage.childOptions?.characterNumber as {
                              maleNumber: number | string;
                              femaleNumber: number | string;
                            }) && Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage.childOptions?.characterNumber as {
                              maleNumber: number | string;
                              femaleNumber: number | string;
                            })
                          }
                          recommendedCharacterNumber={
                            typeof focusedImage?.aiRecommendedChildOptions?.characterNumber === 'object' &&
                            focusedImage?.aiRecommendedChildOptions?.characterNumber !== null &&
                            !Array.isArray(focusedImage?.aiRecommendedChildOptions?.characterNumber)
                              ? `남자 ${focusedImage?.aiRecommendedChildOptions?.characterNumber.maleNumber}명 ∙ 여자 ${focusedImage?.aiRecommendedChildOptions?.characterNumber.femaleNumber}명`
                              : null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'characterNumber',
                              focusedImage?.aiRecommendedChildOptions?.characterNumber
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="프레임"
                          options={{
                            클로즈업: {
                              name: '클로즈업',
                              value: StandardShotChildOptionValueEnum['클로즈업'],
                              isSelected: false
                            },
                            미디엄샷: {
                              name: '미디엄샷',
                              value: StandardShotChildOptionValueEnum['미디엄샷'],
                              isSelected: false
                            },
                            풀샷: {
                              name: '풀샷',
                              value: StandardShotChildOptionValueEnum['풀샷'],
                              isSelected: false
                            },
                            롱샷: {
                              name: '롱샷',
                              value: StandardShotChildOptionValueEnum['롱샷'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={(focusedImage?.childOptions?.shot as IOptionList) ?? null}
                          setSelectedOption={(param) => updateFocusedImageChildOptions('shot', param)}
                          isError={isSaveAttempted && focusedImage?.childOptions?.shot == null}
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={!(focusedImage?.childOptions?.shot as IOptionList)}
                          isLoading={
                            isFetchImageAIRecommendationLoading && !(focusedImage?.childOptions?.shot as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['shot'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'shot',
                              focusedImage?.aiRecommendedChildOptions?.['shot'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="연출"
                          needTooltip={true}
                          tooltipText="주로 1인 전신 컷, 배경에 실제 배경이 아닌 분위기를 표현하는 화려한 연출이 포함됩니다."
                          options={{
                            해당: { name: '해당', value: 'true', isSelected: false },
                            미해당: { name: '미해당', value: 'false', isSelected: false }
                          }}
                          storedChildOptionValue={(focusedImage?.childOptions?.['directed'] as IOptionList) ?? null}
                          setSelectedOption={(param) => updateFocusedImageChildOptions('directed', param)}
                          isError={isSaveAttempted && focusedImage?.childOptions?.directed == null}
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={!(focusedImage?.childOptions?.['directed'] as IOptionList)}
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['directed'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['directed'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'directed',
                              focusedImage?.aiRecommendedChildOptions?.['directed'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="세로앵글"
                          options={{
                            로우앵글: {
                              name: '로우앵글',
                              value: VerticalAngleChildOptionValueEnum['로우앵글'],
                              isSelected: false
                            },
                            하이앵글: {
                              name: '하이앵글',
                              value: VerticalAngleChildOptionValueEnum['하이앵글'],
                              isSelected: false
                            },
                            항공샷: {
                              name: '항공샷',
                              value: VerticalAngleChildOptionValueEnum['항공샷'],
                              isSelected: false
                            },
                            평시각: {
                              name: '평시각',
                              value: VerticalAngleChildOptionValueEnum['평시각'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={
                            (focusedImage?.childOptions?.['verticalAngle'] as IOptionList) ?? null
                          }
                          setSelectedOption={(param) => updateFocusedImageChildOptions('verticalAngle', param)}
                          isError={isSaveAttempted && focusedImage?.childOptions?.verticalAngle == null}
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={!(focusedImage?.childOptions?.['verticalAngle'] as IOptionList)}
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['verticalAngle'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['verticalAngle'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'verticalAngle',
                              focusedImage?.aiRecommendedChildOptions?.['verticalAngle'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="연령대"
                          disabled={Boolean(
                            (focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected
                          )}
                          paperType={ChipPaperType.CHECKBOX_LIST}
                          options={{
                            유아: {
                              name: '유아',
                              value: AgeRangeChildOptionValueEnum['유아'],
                              isSelected: false
                            },
                            청소년: {
                              name: '청소년',
                              value: AgeRangeChildOptionValueEnum['청소년'],
                              isSelected: false
                            },
                            청년: {
                              name: '청년',
                              value: AgeRangeChildOptionValueEnum['청년'],
                              isSelected: false
                            },
                            중장년: {
                              name: '중장년',
                              value: AgeRangeChildOptionValueEnum['중장년'],
                              isSelected: false
                            },
                            노인: {
                              name: '노인',
                              value: AgeRangeChildOptionValueEnum['노인'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={(focusedImage?.childOptions?.['ageRange'] as IOptionList) ?? null}
                          setSelectedOption={(param) => updateFocusedImageChildOptions('ageRange', param)}
                          isError={
                            isSaveAttempted &&
                            focusedImage?.childOptions?.ageRange == null &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={
                            !(focusedImage?.childOptions?.['ageRange'] as IOptionList) &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['ageRange'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['ageRange'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'ageRange',
                              focusedImage?.aiRecommendedChildOptions?.['ageRange'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="중심 동작 부위"
                          disabled={Boolean(
                            (focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected
                          )}
                          paperType={ChipPaperType.CHECKBOX_LIST}
                          options={{
                            손: {
                              name: '손',
                              value: MainMotionPartChildOptionValueEnum['손'],
                              isSelected: false
                            },
                            팔: {
                              name: '팔',
                              value: MainMotionPartChildOptionValueEnum['팔'],
                              isSelected: false
                            },
                            다리: {
                              name: '다리',
                              value: MainMotionPartChildOptionValueEnum['다리'],
                              isSelected: false
                            },
                            발: {
                              name: '발',
                              value: MainMotionPartChildOptionValueEnum['발'],
                              isSelected: false
                            },
                            머리: {
                              name: '머리',
                              value: MainMotionPartChildOptionValueEnum['머리'],
                              isSelected: false
                            },
                            전신: {
                              name: '전신',
                              value: MainMotionPartChildOptionValueEnum['전신'],
                              isSelected: false
                            },
                            상체: {
                              name: '상체',
                              value: MainMotionPartChildOptionValueEnum['상체'],
                              isSelected: false
                            },
                            하체: {
                              name: '하체',
                              value: MainMotionPartChildOptionValueEnum['하체'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={
                            (focusedImage?.childOptions?.['mainMotionPart'] as IOptionList) ?? null
                          }
                          setSelectedOption={(param) => updateFocusedImageChildOptions('mainMotionPart', param)}
                          isError={
                            isSaveAttempted &&
                            focusedImage?.childOptions?.mainMotionPart == null &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={
                            !(focusedImage?.childOptions?.['mainMotionPart'] as IOptionList) &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['mainMotionPart'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['mainMotionPart'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'mainMotionPart',
                              focusedImage?.aiRecommendedChildOptions?.['mainMotionPart'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="표정"
                          disabled={Boolean(
                            (focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected
                          )}
                          needTooltip={true}
                          tooltipText="표정 필터를 활성화하면, 정면 또는 측면 얼굴이 명확히 드러난 이미지만 출력됩니다."
                          paperType={ChipPaperType.FACE_CHECKBOX_LIST}
                          options={{
                            만족: {
                              name: '만족',
                              value: StandardFacialExpressionChildOptionValueEnum['만족'],
                              isSelected: false
                            },
                            미소: {
                              name: '미소',
                              value: StandardFacialExpressionChildOptionValueEnum['미소'],
                              isSelected: false
                            },
                            새침: {
                              name: '새침',
                              value: StandardFacialExpressionChildOptionValueEnum['새침'],
                              isSelected: false
                            },
                            기쁨: {
                              name: '기쁨',
                              value: StandardFacialExpressionChildOptionValueEnum['기쁨'],
                              isSelected: false
                            },
                            폭소: {
                              name: '폭소',
                              value: StandardFacialExpressionChildOptionValueEnum['폭소'],
                              isSelected: false
                            },
                            수줍음: {
                              name: '수줍음',
                              value: StandardFacialExpressionChildOptionValueEnum['수줍음'],
                              isSelected: false
                            },
                            무표정: {
                              name: '무표정',
                              value: StandardFacialExpressionChildOptionValueEnum['무표정'],
                              isSelected: false
                            },
                            놀람: {
                              name: '놀람',
                              value: StandardFacialExpressionChildOptionValueEnum['놀람'],
                              isSelected: false
                            },
                            고민: {
                              name: '고민',
                              value: StandardFacialExpressionChildOptionValueEnum['고민'],
                              isSelected: false
                            },
                            진지: {
                              name: '진지',
                              value: StandardFacialExpressionChildOptionValueEnum['진지'],
                              isSelected: false
                            },
                            혐오: {
                              name: '혐오',
                              value: StandardFacialExpressionChildOptionValueEnum['혐오'],
                              isSelected: false
                            },
                            분노: {
                              name: '분노',
                              value: StandardFacialExpressionChildOptionValueEnum['분노'],
                              isSelected: false
                            },
                            당황: {
                              name: '당황',
                              value: StandardFacialExpressionChildOptionValueEnum['당황'],
                              isSelected: false
                            },
                            공포: {
                              name: '공포',
                              value: StandardFacialExpressionChildOptionValueEnum['공포'],
                              isSelected: false
                            },
                            슬픔: {
                              name: '슬픔',
                              value: StandardFacialExpressionChildOptionValueEnum['슬픔'],
                              isSelected: false
                            },
                            경계: {
                              name: '경계',
                              value: StandardFacialExpressionChildOptionValueEnum['경계'],
                              isSelected: false
                            },
                            좌절: {
                              name: '좌절',
                              value: StandardFacialExpressionChildOptionValueEnum['좌절'],
                              isSelected: false
                            },
                            뻔뻔: {
                              name: '뻔뻔',
                              value: StandardFacialExpressionChildOptionValueEnum['뻔뻔'],
                              isSelected: false
                            },
                            수치: {
                              name: '수치',
                              value: StandardFacialExpressionChildOptionValueEnum['수치'],
                              isSelected: false
                            },
                            의심: {
                              name: '의심',
                              value: StandardFacialExpressionChildOptionValueEnum['의심'],
                              isSelected: false
                            },
                            불안: {
                              name: '불안',
                              value: StandardFacialExpressionChildOptionValueEnum['불안'],
                              isSelected: false
                            },
                            절규: {
                              name: '절규',
                              value: StandardFacialExpressionChildOptionValueEnum['절규'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={
                            (focusedImage?.childOptions?.['facialExpression'] as IOptionList) ?? null
                          }
                          setSelectedOption={(param) => updateFocusedImageChildOptions('facialExpression', param)}
                          isError={
                            isSaveAttempted &&
                            focusedImage?.childOptions?.facialExpression == null &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={
                            !(focusedImage?.childOptions?.['facialExpression'] as IOptionList) &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['facialExpression'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['facialExpression'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'facialExpression',
                              focusedImage?.aiRecommendedChildOptions?.['facialExpression'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="데포르메"
                          disabled={Boolean(
                            (focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected
                          )}
                          options={{
                            해당: { name: '해당', value: 'true', isSelected: false },
                            미해당: { name: '미해당', value: 'false', isSelected: false }
                          }}
                          storedChildOptionValue={(focusedImage?.childOptions?.['deforme'] as IOptionList) ?? null}
                          setSelectedOption={(param) => updateFocusedImageChildOptions('deforme', param)}
                          isError={
                            isSaveAttempted &&
                            focusedImage?.childOptions?.deforme == null &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={
                            !(focusedImage?.childOptions?.['deforme'] as IOptionList) &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['deforme'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['deforme'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'deforme',
                              focusedImage?.aiRecommendedChildOptions?.['deforme'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="가로앵글"
                          disabled={Boolean(
                            (focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected
                          )}
                          paperType={ChipPaperType.CHECKBOX_LIST}
                          options={{
                            정면: {
                              name: '정면',
                              value: HorizontalAngleChildOptionValueEnum['정면'],
                              isSelected: false
                            },
                            정측면: {
                              name: '정측면',
                              value: HorizontalAngleChildOptionValueEnum['정측면'],
                              isSelected: false
                            },
                            측면: {
                              name: '측면',
                              value: HorizontalAngleChildOptionValueEnum['측면'],
                              isSelected: false
                            },
                            후측면: {
                              name: '후측면',
                              value: HorizontalAngleChildOptionValueEnum['후측면'],
                              isSelected: false
                            },
                            후면: {
                              name: '후면',
                              value: HorizontalAngleChildOptionValueEnum['후면'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={
                            (focusedImage?.childOptions?.['horizontalAngle'] as IOptionList) ?? null
                          }
                          setSelectedOption={(param) => updateFocusedImageChildOptions('horizontalAngle', param)}
                          isError={
                            isSaveAttempted &&
                            focusedImage?.childOptions?.horizontalAngle == null &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={
                            !(focusedImage?.childOptions?.['horizontalAngle'] as IOptionList) &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['horizontalAngle'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['horizontalAngle'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'horizontalAngle',
                              focusedImage?.aiRecommendedChildOptions?.['horizontalAngle'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="동작구분"
                          disabled={Boolean(
                            (focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected
                          )}
                          paperType={ChipPaperType.CHECKBOX_LIST_TOGGLE}
                          options={{
                            서있음: {
                              name: '서있음',
                              value: PostureCategoryChildOptionValueEnum['서있음'],
                              isSelected: false
                            },
                            앉음: {
                              name: '앉음',
                              value: PostureCategoryChildOptionValueEnum['앉음'],
                              isSelected: false
                            },
                            누움: {
                              name: '누움',
                              value: PostureCategoryChildOptionValueEnum['누움'],
                              isSelected: false
                            },
                            엎드림: {
                              name: '엎드림',
                              value: PostureCategoryChildOptionValueEnum['엎드림'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={
                            (focusedImage?.childOptions?.['postureCategory'] as IOptionList) ?? null
                          }
                          setSelectedOption={(param) => updateFocusedImageChildOptions('postureCategory', param)}
                          isError={
                            isSaveAttempted &&
                            focusedImage?.childOptions?.postureCategory == null &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={
                            !(focusedImage?.childOptions?.['postureCategory'] as IOptionList) &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected)
                          }
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['postureCategory'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['postureCategory'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'postureCategory',
                              focusedImage?.aiRecommendedChildOptions?.['postureCategory'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="스킨십(12~17+)"
                          disabled={
                            Boolean((focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected) ||
                            isCharacterNumberOne
                          }
                          needTooltip={true}
                          tooltipText={`19+를 제외한 포옹, 키스 등의 접촉 장면을 포함합니다.
              인원수가 2명 이상일 때만 활성화됩니다.`}
                          options={{
                            해당: { name: '해당', value: 'true', isSelected: false },
                            미해당: { name: '미해당', value: 'false', isSelected: false }
                          }}
                          storedChildOptionValue={(focusedImage?.childOptions?.['skinship'] as IOptionList) ?? null}
                          setSelectedOption={(param) => updateFocusedImageChildOptions('skinship', param)}
                          isError={
                            isSaveAttempted &&
                            focusedImage?.childOptions?.skinship == null &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected) &&
                            !isCharacterNumberOne
                          }
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={
                            !(focusedImage?.childOptions?.['skinship'] as IOptionList) &&
                            Boolean(!(focusedImage?.childOptions?.backgroundCut as IOptionList)?.해당.isSelected) &&
                            !isCharacterNumberOne
                          }
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['skinship'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['skinship'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'skinship',
                              focusedImage?.aiRecommendedChildOptions?.['skinship'] as IOptionList
                            )
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="여체"
                          options={{
                            슬랜더: {
                              name: '슬랜더',
                              value: FemaleBodyChildOptionValueEnum['슬랜더'],
                              isSelected: false
                            },
                            글래머: {
                              name: '글래머',
                              value: FemaleBodyChildOptionValueEnum['글래머'],
                              isSelected: false
                            },
                            슈퍼글래머: {
                              name: '슈퍼글래머',
                              value: FemaleBodyChildOptionValueEnum['슈퍼글래머'],
                              isSelected: false
                            },
                            통통: { name: '통통', value: FemaleBodyChildOptionValueEnum['통통'], isSelected: false }
                          }}
                          storedChildOptionValue={(focusedImage?.childOptions?.['femaleBody'] as IOptionList) ?? null}
                          setSelectedOption={(param) => updateFocusedImageChildOptions('femaleBody', param)}
                          isError={isSaveAttempted && focusedImage?.childOptions?.femaleBody == null}
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={!(focusedImage?.childOptions?.['femaleBody'] as IOptionList)}
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['femaleBody'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['femaleBody'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'femaleBody',
                              focusedImage?.aiRecommendedChildOptions?.['femaleBody'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="신체 부위"
                          paperType={ChipPaperType.CHECKBOX_LIST}
                          options={{
                            가슴: {
                              name: '가슴',
                              value: SexualBodyPartChildOptionValueEnum['가슴'],
                              isSelected: false
                            },
                            엉덩이: {
                              name: '엉덩이',
                              value: SexualBodyPartChildOptionValueEnum['엉덩이'],
                              isSelected: false
                            },
                            '여자 성기': {
                              name: '여자 성기',
                              value: SexualBodyPartChildOptionValueEnum['여자 성기'],
                              isSelected: false
                            },
                            '남자 성기': {
                              name: '남자 성기',
                              value: SexualBodyPartChildOptionValueEnum['남자 성기'],
                              isSelected: false
                            },
                            입: {
                              name: '입',
                              value: SexualBodyPartChildOptionValueEnum['입'],
                              isSelected: false
                            },
                            얼굴: {
                              name: '얼굴',
                              value: SexualBodyPartChildOptionValueEnum['얼굴'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={
                            (focusedImage?.childOptions?.['sexualBodyPart'] as IOptionList) ?? null
                          }
                          setSelectedOption={(param) => updateFocusedImageChildOptions('sexualBodyPart', param)}
                          isError={isSaveAttempted && focusedImage?.childOptions?.sexualBodyPart == null}
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={!(focusedImage?.childOptions?.['sexualBodyPart'] as IOptionList)}
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['sexualBodyPart'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['sexualBodyPart'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'sexualBodyPart',
                              focusedImage?.aiRecommendedChildOptions?.['sexualBodyPart'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <AdminCharacterNumberInput
                          characterNumber={
                            typeof focusedImage?.childOptions?.characterNumber === 'object' &&
                            focusedImage?.childOptions?.characterNumber !== null
                              ? (focusedImage.childOptions.characterNumber as {
                                  maleNumber: number | string;
                                  femaleNumber: number | string;
                                })
                              : null
                          }
                          onApplyClick={(values) => updateFocusedImageChildOptions('characterNumber', values)}
                          onChipXButtonClick={() => updateFocusedImageChildOptions('characterNumber', null)}
                          isError={isSaveAttempted && focusedImage?.childOptions?.characterNumber == null}
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={
                            !(focusedImage.childOptions?.characterNumber as {
                              maleNumber: number | string;
                              femaleNumber: number | string;
                            })
                          }
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage.childOptions?.characterNumber as {
                              maleNumber: number | string;
                              femaleNumber: number | string;
                            })
                          }
                          recommendedCharacterNumber={
                            typeof focusedImage?.aiRecommendedChildOptions?.characterNumber === 'object' &&
                            focusedImage?.aiRecommendedChildOptions?.characterNumber !== null &&
                            !Array.isArray(focusedImage?.aiRecommendedChildOptions?.characterNumber)
                              ? `남자 ${focusedImage?.aiRecommendedChildOptions?.characterNumber.maleNumber}명 ∙ 여자 ${focusedImage?.aiRecommendedChildOptions?.characterNumber.femaleNumber}명`
                              : null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'characterNumber',
                              focusedImage?.aiRecommendedChildOptions?.characterNumber
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="프레임"
                          options={{
                            풀샷: {
                              name: '풀샷',
                              value: AdultsOnlyShotChildOptionValueEnum['풀샷'],
                              isSelected: false
                            },
                            미디엄샷: {
                              name: '미디엄샷',
                              value: AdultsOnlyShotChildOptionValueEnum['미디엄샷'],
                              isSelected: false
                            },
                            클로즈업: {
                              name: '클로즈업',
                              value: AdultsOnlyShotChildOptionValueEnum['클로즈업'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={(focusedImage?.childOptions?.shot as IOptionList) ?? null}
                          setSelectedOption={(param) => updateFocusedImageChildOptions('shot', param)}
                          isError={isSaveAttempted && focusedImage?.childOptions?.shot == null}
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={!(focusedImage?.childOptions?.shot as IOptionList)}
                          isLoading={
                            isFetchImageAIRecommendationLoading && !(focusedImage?.childOptions?.shot as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['shot'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'shot',
                              focusedImage?.aiRecommendedChildOptions?.['shot'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="가로앵글"
                          paperType={ChipPaperType.CHECKBOX_LIST}
                          options={{
                            정면: {
                              name: '정면',
                              value: HorizontalAngleChildOptionValueEnum['정면'],
                              isSelected: false
                            },
                            정측면: {
                              name: '정측면',
                              value: HorizontalAngleChildOptionValueEnum['정측면'],
                              isSelected: false
                            },
                            측면: {
                              name: '측면',
                              value: HorizontalAngleChildOptionValueEnum['측면'],
                              isSelected: false
                            },
                            후측면: {
                              name: '후측면',
                              value: HorizontalAngleChildOptionValueEnum['후측면'],
                              isSelected: false
                            },
                            후면: {
                              name: '후면',
                              value: HorizontalAngleChildOptionValueEnum['후면'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={
                            (focusedImage?.childOptions?.['horizontalAngle'] as IOptionList) ?? null
                          }
                          setSelectedOption={(param) => updateFocusedImageChildOptions('horizontalAngle', param)}
                          isError={isSaveAttempted && focusedImage?.childOptions?.horizontalAngle == null}
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={!(focusedImage?.childOptions?.['horizontalAngle'] as IOptionList)}
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['horizontalAngle'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['horizontalAngle'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'horizontalAngle',
                              focusedImage?.aiRecommendedChildOptions?.['horizontalAngle'] as IOptionList
                            )
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label="세로앵글"
                          options={{
                            로우앵글: {
                              name: '로우앵글',
                              value: VerticalAngleChildOptionValueEnum['로우앵글'],
                              isSelected: false
                            },
                            하이앵글: {
                              name: '하이앵글',
                              value: VerticalAngleChildOptionValueEnum['하이앵글'],
                              isSelected: false
                            },
                            항공샷: {
                              name: '항공샷',
                              value: VerticalAngleChildOptionValueEnum['항공샷'],
                              isSelected: false
                            },
                            평시각: {
                              name: '평시각',
                              value: VerticalAngleChildOptionValueEnum['평시각'],
                              isSelected: false
                            }
                          }}
                          storedChildOptionValue={
                            (focusedImage?.childOptions?.['verticalAngle'] as IOptionList) ?? null
                          }
                          setSelectedOption={(param) => updateFocusedImageChildOptions('verticalAngle', param)}
                          isError={isSaveAttempted && focusedImage?.childOptions?.verticalAngle == null}
                        />
                        <AIRecommendedChildOptionValue
                          isVisible={!(focusedImage?.childOptions?.['verticalAngle'] as IOptionList)}
                          isLoading={
                            isFetchImageAIRecommendationLoading &&
                            !(focusedImage?.childOptions?.['verticalAngle'] as IOptionList)
                          }
                          recommendedChildOptionValue={
                            (focusedImage?.aiRecommendedChildOptions?.['verticalAngle'] as IOptionList) ?? null
                          }
                          onClick={() =>
                            updateFocusedImageChildOptions(
                              'verticalAngle',
                              focusedImage?.aiRecommendedChildOptions?.['verticalAngle'] as IOptionList
                            )
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
                {/* 필터 end */}

                <div>
                  <div
                    style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: '8px' }}
                  >
                    <Text variant="subtitle" fontWeight="bold">
                      관련 태그
                    </Text>
                    <Text variant="body1" color={colorSet.gray400}>
                      {'(Optional)'}
                    </Text>
                  </div>
                  <SearchInput
                    placeholder="태그 입력 후 스페이스바를 누르세요."
                    value={tagInputKeyword}
                    options={tagAutocompleteList}
                    onKeyDown={handleTagKeyDown}
                    onInputChange={handleTagInputKeywordChange}
                    onAutocompleteChange={handleTagAutocompleteChange}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: `10px`,
                      width: '100%',
                      boxSizing: 'border-box',
                      marginTop: focusedImage.tags && focusedImage.tags.length > 0 ? 10 : 0
                    }}
                  >
                    {focusedImage.tags && focusedImage.tags.length > 0
                      ? focusedImage.tags.map((item, index) => {
                          return (
                            <div
                              key={index}
                              style={{
                                height: 28,
                                border: `1px solid ${colorSet.gray300}`,
                                borderRadius: '6px',
                                padding: '0px 10px',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <Text variant="body1" marginRight="2px">
                                {item}
                              </Text>
                              <XGray400Icon
                                onClick={() => {
                                  deleteFocusedImageTag(item);
                                }}
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                              />
                            </div>
                          );
                        })
                      : null}
                  </div>

                  <div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 4, marginTop: 24, marginBottom: 10 }}>
                      <StarsSecondaryIcon />
                      <Text variant="body1" color={colorSet.gray500}>
                        AI 추천 태그
                      </Text>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: `10px`,
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    >
                      {isFetchImageAIRecommendationLoading ? <LoadingSpinner /> : null}
                      {focusedImage.aiRecommendedTags && focusedImage.aiRecommendedTags.length > 0
                        ? focusedImage.aiRecommendedTags
                            .filter((aiTag) => !focusedImage.tags?.includes(aiTag)) //이미 tags에 추가된 값은 제외하기
                            .map((item, index) => {
                              return (
                                <AIRecommendedTag
                                  key={index}
                                  recommendedTag={item}
                                  onClick={() => {
                                    addFocusedImageTag(item);
                                  }}
                                />
                              );
                            })
                        : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <AdminIssueReportModal
        isVisible={isIssueReportModalVisible}
        onClose={() => {
          setIsIssueReportModalVisible(false);
        }}
        focusedImage={focusedImage}
        getImageIssueReportList={getImageIssueReportList}
        updateCheckedIssueReportIds={updateCheckedIssueReportIds}
      />

      <GuideModal
        isVisible={isModalVisible}
        onClick={handleModalConfirmClick}
        onClose={() => {
          if (
            modalType === AdminModalType.IMAGE_UPLOAD_SUCCESS ||
            modalType === AdminModalType.IMAGE_EDIT_SUCCESS ||
            modalType === AdminModalType.OMIT_REQUIRED_INPUT
          ) {
            handleModalConfirmClick();
          } else {
            setIsModalVisible(false);
          }
        }}
        imgSrc={
          modalType === AdminModalType.IMAGE_UPLOAD_SUCCESS || modalType === AdminModalType.IMAGE_EDIT_SUCCESS
            ? check_circle_primary
            : undefined
        }
        title={modalTitle}
        content={modalContent}
        buttonText={createModalButtonText()}
        buttonVariant={createModalButtonVariant()}
        buttonCount={createModalButtonCount()}
      />
    </>
  );
};

export default ImageUpsertPage;
